import { AxiosError } from 'axios';
import { ApiException } from 'src/api/FiretrolApiClient';

export interface ApiValidationErrorItem {
  message?: string;
  rule?: string;
  field?: string;
}

export interface ParsedValidationErrors {
  messages: string[];
  fieldErrors: Record<string, string[]>;
}

// A 400/422 body is not guaranteed to be JSON (empty bodies and plain-text
// proxy errors both reach here), and a throw would escape getErrorMessage.
const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const parse422Errors = (
  error: unknown,
): ParsedValidationErrors | null => {
  const axiosError = error as AxiosError<any> | undefined;
  const status = axiosError?.response?.status || axiosError?.status;

  if (status !== 422 && status !== 400) return null;

  const response =
    typeof axiosError?.response === 'string'
      ? parseJson(axiosError.response)
      : axiosError.response;
  const body = axiosError?.response?.data || response;

  if (body == undefined) {
    return null;
  }

  // FluentValidation-backed controllers return BadRequest(errors.Select(e =>
  // e.ErrorMessage)) — a bare JSON string array with no envelope.
  if (typeof body === 'string' && body.trim().length) {
    return { messages: [body], fieldErrors: {} };
  }

  if (Array.isArray(body)) {
    const bare = body.filter(
      (message): message is string =>
        typeof message === 'string' && message.trim().length > 0,
    );
    if (bare.length > 0) return { messages: bare, fieldErrors: {} };
  }

  const data = body as
    | { errors?: ApiValidationErrorItem[]; message?: string }
    | undefined;

  const items = Array.isArray(data?.errors) ? data?.errors : [];

  const messages: string[] = [];
  const fieldErrors: Record<string, string[]> = {};

  if (items.length > 0) {
    for (const item of items) {
      const msg = item?.message || 'Invalid input';
      messages.push(msg);
      const field = item?.field;
      if (field) {
        if (!fieldErrors[field]) fieldErrors[field] = [];
        fieldErrors[field].push(msg);
      }
    }
  } else if (typeof data?.message === 'string' && data.message.trim().length) {
    messages.push(data.message);
  } else {
    messages.push('The request contains invalid data.');
  }

  return { messages, fieldErrors };
};

// jsdom's Blob has no text(), so the FileReader branch is the one the unit
// tests exercise.
const blobToText = (blob: Blob): Promise<string> =>
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });

// Axios keeps an error body as a Blob whenever the request used
// responseType: "blob" (NSwag emits that for endpoints with no declared
// success payload), and the generated client passes it straight into
// ApiException.response, where the synchronous message readers cannot reach
// it. Decoding has to happen here, in the async interceptor.
export const decodeBlobErrorBody = async (error: unknown): Promise<unknown> => {
  const response = (error as AxiosError | undefined)?.response;
  const data = response?.data;

  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      (response as { data: unknown }).data = await blobToText(data);
    } catch {
      return error;
    }
  }

  return error;
};

export const isApiErrorStatus = (err: unknown, status: number): boolean =>
  ApiException.isApiException(err) && err.status === status;
